import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  browserRecoveryStorage,
  findCreateRecoveriesForProduct,
  type CreateRecoveryRecord,
} from "@/lib/wizard-create-recovery";

/**
 * Post-create attention notice on Product Detail.
 *
 * A product created by the wizard can be left with unfinished post-create work:
 * images still attached to the draft, a channel draft that failed, or an
 * unlinked supplier. The recovery record for that work lives in this browser,
 * scoped to the draft it was written for, so opening the product showed nothing.
 *
 * This only READS that record and links back to the draft. It never retries,
 * never clears, and never claims a step failed — the record only says which
 * stage the product reached.
 */

const STAGE_TEXT: Record<CreateRecoveryRecord["stage"], string> = {
  pending_create: "A previous create for this draft did not report back, so its outcome is unknown.",
  canonical: "Some catalogue details were not confirmed after the product was created.",
  media: "This product's images were never moved across from the draft.",
  post_create: "Some post-create steps (supplier link, channel listings or channel images) did not finish.",
};

export default function PostCreateAttentionBanner({
  orgId,
  productId,
}: {
  orgId: string;
  productId: string;
}) {
  const navigate = useNavigate();
  const [records, setRecords] = useState<CreateRecoveryRecord[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const found = findCreateRecoveriesForProduct(browserRecoveryStorage(), orgId, productId);
    setRecords(found);
    setDismissed(false);
  }, [orgId, productId]);

  if (dismissed || records.length === 0) return null;
  const record = records[0];

  return (
    <div
      className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg"
      role="status"
      data-testid="post-create-attention"
    >
      <div className="flex items-start gap-2.5">
        <i className="ri-error-warning-line text-amber-600 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">This product still has unfinished steps</p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">{STAGE_TEXT[record.stage]}</p>
          <p className="text-[11px] text-amber-700 mt-1">
            The product itself exists and is not affected. Nothing here is a marketplace change.
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate(`/products/create?draft=${record.draftId}`)}
          className="px-3 py-1.5 bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5"
        >
          <i className="ri-refresh-line" /> Finish the remaining steps
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="px-3 py-1.5 bg-white border border-amber-200 hover:bg-amber-100 text-amber-800 text-xs font-medium rounded-md whitespace-nowrap"
        >
          Hide for now
        </button>
      </div>
    </div>
  );
}